import React, { useContext, useEffect, useRef, useState } from 'react';
import './styles.scss';
import { OverlayEnum } from '../../enums';
import { OverlayType } from './interfaces';
import SspProviderContext from '../../utils/SspProvider/context';
import { setOverlay } from '../../utils/SspProvider/dispatchFunctions';
import Slider from 'react-slick';
import FileSaver from 'file-saver';
import sliderSettings from '../../utils/SliderSettings';
import VideoPlayer from '../VideoPlayer';
import i18next from 'i18next';
import Pagination from '../Pagination';
import MultiSlideOverlay from './MultiSlideOverlay';
import { marked } from 'marked';
import { SET_VIDEO_STATE_ACTION } from '../../utils/SspProvider/store';
import { FullWidthButton } from '../AutostadtTabComponents/Button';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let activeVideoId: string | null = null;

interface SlickSlider extends Slider {
    slickGoTo: (index: number) => void;
}

export default function Overlay(): JSX.Element {
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const store = useContext(SspProviderContext);
    const overlayContent = store.state.overlay;

    const [currentOverlay, setCurrentOverlay] = useState<OverlayType | null>(null);
    const [selectedImage, setSelectedImage] = useState(currentOverlay?.imageIndex);
    const [selectedOverlayImage, setSelectedOverlayImage] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [isButtonEnabled, setIsButtonEnabled] = useState(true); // New state variable
    const [showFullDescriptionUpper, setShowFullDescriptionUpper] = useState(false);
    const [showFullDescriptionLower, setShowFullDescriptionLower] = useState(false);

    const getLimitedText = (text: string, limit: number) => {
        if (text.length > limit) {
            return text.substring(0, limit) + '...';
        }
        return text;
    };

    const overlaySliderRef = useRef<SlickSlider | null>(null);
    const mainSliderRef = useRef<SlickSlider | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const totalPages = currentOverlay?.multiSlideOverlay?.length ? currentOverlay.multiSlideOverlay.length + 1 : 0;

    const disclaimerTexts: any = {
        1: 'Die angegebenen Verbrauchs- und Emissionswerte wurden nach den gesetzlich vorgeschriebenen Messverfahren ermittelt. Seit dem 1. September 2017 werden bestimmte Neuwagen bereits nach dem weltweit harmonisierten Prüfverfahren für Personenwagen und leichte Nutzfahrzeuge (Worldwide Harmonized Light Vehicles Test Procedure, WLTP), einem realistischeren Prüfverfahren zur Messung des Kraftstoffverbrauchs und der CO₂-Emissionen, typgenehmigt. Ab dem 1. September 2018 wird der WLTP schrittweise den neuen europäischen Fahrzyklus (NEFZ) ersetzen. Wegen der realistischeren Prüfbedingungen sind die nach dem WLTP gemessenen Kraftstoffverbrauchs- und CO2 - Emissionswerte in vielen Fällen höher als die nach dem NEFZ gemessenen. Dadurch können sich ab 1. September 2018 bei der Fahrzeugbesteuerung entsprechende Änderungen ergeben. Weitere Informationen zu den Unterschieden zwischen WLTP und NEFZ finden Sie unter https: //www.volkswagen.de/wltp. Aktuell sind noch die NEFZ-Werte verpflichtend zu kommunizieren. Soweit es sich um Neuwagen handelt, die nach WLTP typgenehmigt sind, werden die NEFZ-Werte von den WLTP-Werten abgeleitet. Die zusätzliche Angabe der WLTP-Werte kann bis zu deren verpflichtender Verwendung freiwillig erfolgen. Soweit die NEFZ-Werte als Spannen angegeben werden, beziehen sie sich nicht auf ein einzelnes, individuelles Fahrzeug und sind nicht Bestandteil des Angebotes. Sie dienen allein Vergleichszwecken zwischen den verschiedenen Fahrzeugtypen. Zusatzausstattungen und Zubehör (Anbauteile, Reifenformat, usw.) können relevante Fahrzeugparameter, wie z. B. Gewicht, Rollwiderstand und Aerodynamik verändern und neben Witterungs- und Verkehrsbedingungen sowie dem individuellen Fahrverhalten den Kraftstoffverbrauch, den Stromverbrauch, die CO₂-Emissionen und die Fahrleistungswerte eines Fahrzeugs beeinflussen.Weitere Informationen zum offiziellen Kraftstoffverbrauch und den offiziellen, spezifischen CO₂-Emissionen neuer Personenkraftwagen können dem Leitfaden über den Kraftstoffverbrauch, die CO₂-Emissionen und den Stromverbrauch neuer Personenkraftwagen entnommen werden, der an allen Verkaufsstellen und bei der DAT Deutsche Automobil Treuhand GmbH, Hellmuth-Hirth-Str. 1, 73760 Ostfildern-Scharnhausen https://www.dat.de unentgeltlich erhältlich ist.',
        2: 'Effizienzklassen bewerten Fahrzeuge anhand der CO2-Emissionen unter Berücksichtigung des Fahrzeugleergewichts. Fahrzeuge, die dem Durchschnitt entsprechen, werden mit D eingestuft. Fahrzeuge, die besser sind als der heutige Durchschnitt werden mit A+++, A++, A+, A, B oder C eingestuft. Fahrzeuge, die schlechter als der Durchschnitt sind, werden mit E, F oder G beschrieben. Die hier gemachten Angaben beziehen sich jeweils auf die EG-Typgenehmigung des gewählten Modells und dessen Serienausstattung gem. Richtlinie 2007/46/EG. Von Ihnen im Zuge der Konfiguration gewählte Sonderausstattung kann dazu führen, dass Ihr konfiguriertes Modell aufgrund der gewählten Ausstattung einem anderen genehmigten Typ entspricht, als dies ohne gewählte Sonderausstattung der Fall wäre. Daraus können sich Abweichungen der Angaben für Ihr konfiguriertes Modell ergeben. Bei den angegebenen CO2-Werten handelt es sich um die Werte, die im Rahmen der Typgenehmigung des Fahrzeugs ermittelt wurden.',
    };

    useEffect(() => {
        if (currentOverlay?.setImageIndexEventType)
            window.dispatchEvent(
                new CustomEvent(currentOverlay.setImageIndexEventType, { detail: { index: selectedImage } })
            );
    }, [currentOverlay?.setImageIndexEventType, selectedImage]);

    useEffect(() => {
        setSelectedImage(currentOverlay?.imageIndex);
    }, [currentOverlay?.imageIndex]);

    useEffect(() => {
        if (overlayContent) {
            setCurrentOverlay(overlayContent);
        }
    }, [overlayContent]);

    window.addEventListener('resize', () => {
        if (overlayRef.current) {
            (overlayRef.current as HTMLDivElement).style.height = window.innerHeight + 'px';
        }
    });

    const updateSlickListHeight = () => {
        if (currentOverlay?.multiSlideOverlay) {
            const pageOverlayContainer = document.querySelector('.inside');

            const activeSlide = pageOverlayContainer
                ? pageOverlayContainer.querySelector(
                      `:scope > .slick-slider > .slick-list > .slick-track > [data-index="${currentPage}"]`
                  )
                : null;

            const slickList = pageOverlayContainer ? pageOverlayContainer.querySelector('.slick-list') : null;

            if (activeSlide && slickList && activeSlide instanceof HTMLElement) {
                (slickList as HTMLElement).style.height = `${activeSlide.offsetHeight}px`;
            }
        }
    };

    useEffect(() => {
        const pageOverlayContainer = document.querySelector('.inside');
        const activeSlide = pageOverlayContainer?.querySelector(
            `:scope > .slick-slider > .slick-list > .slick-track > [data-index="${currentPage}"]`
        );

        if (!activeSlide) return;

        const observer = new ResizeObserver(() => {
            updateSlickListHeight();
        });

        observer.observe(activeSlide);

        return () => {
            observer.disconnect();
        };
    }, [currentPage, selectedOverlayImage, updateSlickListHeight]);

    const extUrl = currentOverlay?.media;

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [currentPage]);

    function redirectToUrl(url: unknown) {
        if (typeof url === 'string') {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    }

    if (!currentOverlay) {
        return <></>;
    }

    const isEnum = currentOverlay && currentOverlay.enum !== undefined;
    const download = currentOverlay && currentOverlay.download;
    const hasAssistantSystems = currentOverlay.assistants && currentOverlay.assistants.length > 0;
    const hasPackageContents = currentOverlay.packageContents && currentOverlay.packageContents.length > 0;

    const equipItem = currentOverlay && (currentOverlay.descriptionUpper || hasAssistantSystems || hasPackageContents);

    const disclaimer = currentOverlay && currentOverlay.disclaimer;
    const info = currentOverlay.infoText && !isEnum;

    const overlayWithTitle = equipItem || currentOverlay.carDetails || currentOverlay.galleryImages;
    const noClose =
        overlayWithTitle ||
        (store.state.overlay &&
            store.state.overlay.enum !== undefined &&
            [OverlayEnum.Loading, OverlayEnum.WrongVIN].includes(store.state.overlay.enum));

    const downloadImage = async (index: number) => {
        const url = currentOverlay.galleryImages && currentOverlay.galleryImages[index].asAsset.url;
        const fileType =
            currentOverlay.galleryImages && currentOverlay.galleryImages[index].asAsset.fileProperties.fileType;
        const name =
            currentOverlay.galleryImages &&
            currentOverlay.galleryImages[index].asAsset.metadata.str('name') + '.' + fileType;
        if (url && name) {
            try {
                const response = await fetch(url, {
                    headers: {
                        'Cache-Control': 'no-cache',
                    },
                });
                const blob = await response.blob();
                FileSaver.saveAs(blob, name);
            } catch (error) {
                console.error('Error downloading the image:', error);
            }
        }
    };

    function OverlayContent(overlay: OverlayType) {
        const line = <div className='overlay-line' />;

        switch (overlay.enum) {
            case OverlayEnum.HomeScreen:
                return (
                    <div className='homescreen'>
                        <h3>Möchten Sie diese Anwendung als App-Favoriten auf Ihrem Homescreen ablegen?</h3>
                        {line}
                        <div>
                            <p>Gehen Sie hierfür einfach wie folgt vor:</p>
                            <ol>
                                <li>
                                    Tippen Sie auf das Hochladen-Icon (Rechteck mit Pfeil), welches sich in den
                                    Browseroptionen befindet.
                                </li>
                                <li>
                                    Scrollen Sie nach unten bis zur Option "Zum Home-Bildschirm" und tippen Sie hierauf.
                                    Klicken Sie nun auf Hinzufügen und die Anwendung wird als App auf Ihrem Homescreen
                                    hinterlegt.
                                </li>
                            </ol>
                        </div>
                        <FullWidthButton
                            type='primary'
                            color='blue'
                            text='ZURÜCK ZUR WEB-APP'
                            onClick={() => {
                                activeVideoId = null;
                                localStorage.setItem('popupShown', 'true');
                                setOverlay(store, null);
                            }}
                        />
                    </div>
                );
            case OverlayEnum.InfoChecklist:
                return (
                    <div className='overlay-content checklist'>
                        <h1>HINWEIS ZUR CHECKLISTE</h1>
                        {line}
                        <p>
                            In allen Volkswagen Modellen sind Warndreieck, Verbandkasten und Warnweste bereits
                            serienmäßig beigelegt und müssen nicht zusätzlich von Ihnen mitgebracht werden.
                        </p>
                    </div>
                );
            case OverlayEnum.AdditionalInfo:
                return (
                    <div className='overlay-content checklist'>
                        <h1>ZUSÄTZLICHE INFORMATIONEN</h1>
                        {line}
                        <p>{overlay.info}</p>
                    </div>
                );

           /* case OverlayEnum.Loading:
                return <InitLoading />; */

            case OverlayEnum.WrongVIN:
                return (
                    <div className='overlay-content'>
                        <h1>KEIN ZUGRIFF</h1>
                        {line}
                        <p>
                            Sie haben Ihr Fahrzeug bereits in der Autostadt abgeholt? Dann fährt Ihr Volkswagen jetzt in
                            der Volkswagen App weiter.
                        </p>
                    </div>
                );
            case OverlayEnum.ExternalContent:
                return (
                    <div className='overlay-content external-content'>
                        <h2>Externe Inhalte</h2>
                        <div className='line' />
                        <p className='bold'>
                            Wenn Sie auf diesen Link gehen, verlassen Sie die Seiten der Autostadt GmbH. Es gelten dann
                            gegebenenfalls etwaige Nutzungsbedingungen des Dritten.
                        </p>
                        <p>
                            Die Autostadt GmbH hat keinen Einfluss darauf, welche Daten auf der von einem Dritten
                            angebotenen Seite von Ihnen erhoben, gespeichert oder verarbeitet werden. Nähere
                            Informationen hierzu können Sie gegebenenfalls in der Datenschutzerklärung des Anbieters der
                            externen Webseite finden.
                        </p>
                        <div className='buttons'>
                            <button
                                className='cancel'
                                onClick={() => {
                                    activeVideoId = null;
                                    setOverlay(store, null);
                                }}
                            >
                                ABBRECHEN
                            </button>
                            <button
                                className='accept'
                                id='acceptBtn'
                                onClick={() => {
                                    redirectToUrl(extUrl);
                                    setOverlay(store, null);
                                }}
                            >
                                AKZEPTIEREN
                            </button>
                        </div>
                    </div>
                );
            default:
                return <></>;
        }
    }

    const handleClick = (index: number, asset: string) => {
        if (overlaySliderRef.current) {
            overlaySliderRef.current?.slickGoTo(index);
            setSelectedOverlayImage(index);
        }
        if (asset === 'img') {
            store.dispatch({ type: SET_VIDEO_STATE_ACTION, value: null });
        } else {
            store.dispatch({ type: SET_VIDEO_STATE_ACTION, value: asset });
        }
    };

    const handleMultiSlideClick = (index: number) => {
        if (!isButtonEnabled) return;
        setIsButtonEnabled(false);
        setCurrentPage(index);
        mainSliderRef.current?.slickGoTo(index);
        setTimeout(() => setIsButtonEnabled(true), 600);
    };

    const handleBack = () => {
        if (!isButtonEnabled) return;
        setIsButtonEnabled(false);

        if (mainSliderRef.current) {
            mainSliderRef.current?.slickPrev();
        }

        if (currentPage > 0) {
            setCurrentPage(prev => prev - 1);
        }
        setTimeout(() => setIsButtonEnabled(true), 600);
    };

    const handleForward = () => {
        if (!isButtonEnabled) return;
        setIsButtonEnabled(false);

        if (mainSliderRef.current) {
            mainSliderRef.current?.slickNext();
        }

        if (currentPage < totalPages - 1) {
            setCurrentPage(prev => prev + 1);
        }
        setTimeout(() => setIsButtonEnabled(true), 600);
    };

    return (
        <div
            className={`overlay-container${
                isEnum &&
                currentOverlay.enum !== OverlayEnum.ExternalContent &&
                currentOverlay.enum !== OverlayEnum.HomeScreen
                    ? ' blue'
                    : ' not-blue'
            }${download ? ' download' : ''}`}
            ref={overlayRef}
        >
            <div
                className={`overlay${overlayWithTitle ? ' full-with-title' : ''}${
                    disclaimer ? ' black' : ' white'
                }${disclaimer || info ? ' half' : ''}${
                    currentOverlay.enum === OverlayEnum.ExternalContent ? ' ext' : ''
                } ${currentOverlay.enum === OverlayEnum.HomeScreen ? ' homescreen' : ''}`}
                onClick={e => {
                    e.stopPropagation();
                }}
            >
                {isEnum && currentOverlay.enum !== null && OverlayContent(currentOverlay)}
                {currentOverlay.galleryImages && (
                    <div className='overlay-content'>
                        <div className='title'>
                            <h5>{i18next.t('Gallery')}</h5>
                        </div>
                        <Slider
                            {...sliderSettings}
                            initialSlide={currentOverlay.imageIndex}
                            afterChange={current => {
                                setSelectedImage(current);
                            }}
                        >
                            {currentOverlay.galleryImages.map((image, index) => (
                                <div key={`open-gallery- ${index}`} className='open-gallery'>
                                    <img
                                        className='open-gallery-img'
                                        src={image.asAsset.url}
                                        alt={`open-gallery- ${index}`}
                                    />
                                </div>
                            ))}
                        </Slider>
                        <div
                            className='download-button'
                            onClick={() => selectedImage !== undefined && downloadImage(selectedImage)}
                        >
                            <svg>
                                <use xlinkHref='#download-icon' />
                            </svg>
                        </div>
                    </div>
                )}
                {currentOverlay && equipItem && (
                    <div className='overlay-content'>
                        <div className='title'>
                            <h5>{currentOverlay.title ?? ''}</h5>
                        </div>

                        <div
                            className='inside'
                            ref={scrollContainerRef}
                            style={{ overflowY: 'auto', maxHeight: '100vh' }}
                        >
                            <Slider
                                swipe={false}
                                slidesToShow={1}
                                infinite={false}
                                arrows={false}
                                dots={false}
                                ref={mainSliderRef}
                                afterChange={current => setCurrentPage(current)}
                            >
                                <div>
                                    {currentOverlay.headline && (
                                        <h5
                                            dangerouslySetInnerHTML={{
                                                __html: marked.parse(currentOverlay.headline) as string,
                                            }}
                                        />
                                    )}
                                    {currentOverlay.descriptionUpper && (
                                        <div>
                                            <p
                                                dangerouslySetInnerHTML={{
                                                    __html: marked.parse(
                                                        showFullDescriptionUpper
                                                            ? currentOverlay.descriptionUpper
                                                            : getLimitedText(currentOverlay.descriptionUpper, 550),
                                                        { breaks: true }
                                                    ) as string,
                                                }}
                                            />
                                            {currentOverlay.descriptionUpper.length > 550 && (
                                                <>
                                                    {!showFullDescriptionUpper ? (
                                                        <button
                                                            className='underlined-btn'
                                                            onClick={() => setShowFullDescriptionUpper(true)}
                                                        >
                                                            {i18next.t('showMore')}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className='underlined-btn'
                                                            onClick={() => setShowFullDescriptionUpper(false)}
                                                        >
                                                            {i18next.t('showLess')}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {
                                        <ul>
                                            {currentOverlay.packageContents &&
                                                currentOverlay.packageContents.map((item, index) => (
                                                    <li key={index}>{item.asModel.str('layertitle')}</li>
                                                ))}
                                        </ul>
                                    }

                                    {currentOverlay.overlayImages && (
                                        <Slider
                                            fade={true}
                                            adaptiveHeight={true}
                                            ref={overlaySliderRef}
                                            arrows={false}
                                            beforeChange={(current, next) => {
                                                setSelectedOverlayImage(next);
                                                if (
                                                    currentOverlay.overlayImages &&
                                                    currentOverlay.overlayImages[current].asAsset.fileFamily === 'video'
                                                ) {
                                                    window.dispatchEvent(new CustomEvent('inner-slider-swipe'));
                                                } else {
                                                    window.dispatchEvent(new CustomEvent('inner-slider-swipe'));
                                                }
                                            }}
                                        >
                                            {currentOverlay.overlayImages.map((img, index) =>
                                                img.asAsset.fileFamily === 'video' ? (
                                                    <div className='large' key={`overlay-video- ${index}`}>
                                                        <VideoPlayer
                                                            videoId={img.asAsset.url}
                                                            filetype={img.asAsset.fileProperties.fileType}
                                                            link={img.asAsset.url}
                                                            placeholder={img.asAsset.posterUrl}
                                                            autoPlay={selectedOverlayImage === index}
                                                        />
                                                    </div>
                                                ) : (
                                                    <img
                                                        className='large'
                                                        src={img.asAsset.url}
                                                        key={`overlay-img-${index}`}
                                                        alt={img.name}
                                                    />
                                                )
                                            )}
                                        </Slider>
                                    )}

                                    {currentOverlay.overlayImages && currentOverlay.overlayImages.length > 1 && (
                                        <div className='img-gallery'>
                                            {currentOverlay.overlayImages.map((img, index) =>
                                                img.asAsset.fileFamily === 'video' ? (
                                                    <div
                                                        className='video-container'
                                                        key={`img-gallery- ${index}`}
                                                        onClick={() => handleClick(index, img.asAsset.url)}
                                                    >
                                                        <img
                                                            className={`${selectedOverlayImage === index ? 'active' : ''}`}
                                                            src={img.asAsset.posterUrl}
                                                            alt={img.name}
                                                        />
                                                        <svg>
                                                            <use xlinkHref={'#play'} />
                                                        </svg>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className='img-container'
                                                        key={`img-gallery- ${index}`}
                                                        onClick={() => handleClick(index, 'img')}
                                                    >
                                                        <img
                                                            className={`${selectedOverlayImage === index ? 'active' : ''}`}
                                                            src={img.asAsset.url}
                                                            alt={img.name}
                                                        />
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    )}
                                    {currentOverlay.descriptionLower && (
                                        <div>
                                            <p
                                                dangerouslySetInnerHTML={{
                                                    __html: marked.parse(
                                                        showFullDescriptionLower
                                                            ? currentOverlay.descriptionLower
                                                            : getLimitedText(currentOverlay.descriptionLower, 550),
                                                        { breaks: true }
                                                    ) as string,
                                                }}
                                            />
                                            {currentOverlay.descriptionLower.length > 550 && (
                                                <>
                                                    {!showFullDescriptionLower ? (
                                                        <button
                                                            className='underlined-btn'
                                                            onClick={() => setShowFullDescriptionLower(true)}
                                                        >
                                                            {i18next.t('showMore')}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className='underlined-btn'
                                                            onClick={() => setShowFullDescriptionLower(false)}
                                                        >
                                                            {i18next.t('showLess')}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {currentOverlay.link && (
                                        <button
                                            className='blue'
                                            onClick={() => {
                                                setOverlay(store, {
                                                    enum: OverlayEnum.ExternalContent,
                                                    media: currentOverlay.link,
                                                });
                                            }}
                                        >
                                            {i18next.t('LearnMore')}
                                        </button>
                                    )}
                                </div>

                                {currentOverlay.multiSlideOverlay &&
                                    currentOverlay.multiSlideOverlay.map((slide, index) => (
                                        <MultiSlideOverlay
                                            currentOverlay={currentOverlay}
                                            currentOverlayImages={currentOverlay.overlayImages}
                                            index={index}
                                            slide={slide}
                                            key={index}
                                        />
                                    ))}
                            </Slider>
                            {currentOverlay.multiSlideOverlay !== undefined &&
                                currentOverlay.multiSlideOverlay.length > 0 && (
                                    <div className='multi-slide-nav-bar'>
                                        <svg
                                            className={`left ${currentPage === 0 ? 'disabled' : ''}`}
                                            onClick={handleBack}
                                        >
                                            <use xlinkHref={'#chevron-narrow-right'}></use>{' '}
                                        </svg>
                                        <Pagination
                                            currentButton={currentPage}
                                            numberOfPages={totalPages}
                                            onClick={handleMultiSlideClick}
                                        />
                                        <svg
                                            className={`right ${currentPage === totalPages - 1 ? 'disabled' : ''}`}
                                            onClick={handleForward}
                                        >
                                            <use xlinkHref={'#chevron-narrow-right'}></use>{' '}
                                        </svg>
                                    </div>
                                )}
                        </div>
                    </div>
                )}

                {disclaimer && currentOverlay.disclaimer && currentOverlay.disclaimerList && (
                    <p title={currentOverlay.disclaimer.toString()}>
                        {currentOverlay.disclaimerList[currentOverlay.disclaimer]}
                    </p>
                )}

                {disclaimer && currentOverlay.disclaimer && (
                    <p title={currentOverlay.disclaimer.toString()}>{disclaimerTexts[currentOverlay.disclaimer]}</p>
                )}

                {info && (
                    <>
                        {currentOverlay.infoHeadline && (
                            <h1 className='info-headline'>{currentOverlay.infoHeadline}</h1>
                        )}
                        <p className='info-text'>{currentOverlay.infoText}</p>
                    </>
                )}

                {!noClose && (
                    <button
                        className='overlay-close-button'
                        onClick={() => {
                            activeVideoId = null;
                            setOverlay(store, null);
                            store.dispatch({ type: SET_VIDEO_STATE_ACTION, value: null });
                            if (currentOverlay.enum === OverlayEnum.HomeScreen)
                                localStorage.setItem('popupShown', 'true');
                        }}
                    >
                        <svg>
                            <use xlinkHref={currentOverlay.enum === OverlayEnum.HomeScreen ? '#as-close' : '#close'} />
                        </svg>
                    </button>
                )}

                {overlayWithTitle && (
                    <button
                        className='back-button'
                        onClick={() => {
                            activeVideoId = null;
                            setOverlay(store, null);
                            store.dispatch({ type: SET_VIDEO_STATE_ACTION, value: null });
                        }}
                    >
                        <svg>
                            <use xlinkHref='#back-chevron' />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
}
